import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFeature, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	AddIcon,
	Button,
	Card,
	ContextMenu,
	Empty,
	MagicWandIcon,
	Popover,
	SettingsSection,
	Tabs,
} from '@treams/treams-ui';

import { sortByOrderThenTitle } from '../../../competencies/sortJobCompetenciesByOrder';
import JobCompetencyEditorModal from './JobCompetencyEditor';
import JobCompetencyGeneratorModal from './JobCompetencyGenerator';
import JobCompetencyRow from './JobCompetencyRow';

export default function JobCompetenciesEditor(props: { jobTitle: graphql.JobTitle }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const hasAI = useFeature(graphql.FeatureName.Ai);

	const [adding, setAdding] = useState(false);
	const [generating, setGenerating] = useState(false);

	const levels = _.sortBy(
		_.uniqBy(
			props.jobTitle.competencies.map(({ level }) => level),
			(level) => level?.id,
		),
		(competency) => localize(competency?.title ?? [])?.text ?? '',
	);

	const [selectedLevel, setSelectedLevel] = useState(levels[0]?.id);
	const [isAddDropdownHidden, setAddDropdownHidden] = useState(true);

	const competencies = sortByOrderThenTitle(
		props.jobTitle.competencies.filter((competency) => competency.level?.id === selectedLevel),
		(competency) => competency.order,
		(competency) => localize(competency.competency.title).text.toLowerCase(),
	);

	useEffect(() => {
		if (competencies.length === 0 && levels.length > 0) {
			setSelectedLevel(levels[0]?.id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [competencies.length, levels.length]);

	const dropdown = (
		<Popover.Target
			visible={!isAddDropdownHidden}
			contents={
				<ContextMenu>
					<ContextMenu.Item
						icon={<AddIcon />}
						onClick={() => {
							setAdding(true);
							setAddDropdownHidden(true);
						}}
						title={t('settings.jobCompetencies.addExisting')}
					/>

					<ContextMenu.Item
						icon={<MagicWandIcon />}
						onClick={() => {
							setGenerating(true);
							setAddDropdownHidden(true);
						}}
						title={t('settings.jobCompetencies.addGenerated')}
					/>
				</ContextMenu>
			}
		>
			<Button
				title={t('settings.jobCompetencies.addButton.title')}
				icon={<AddIcon />}
				onClick={() => {
					if (hasAI) {
						setAddDropdownHidden(!isAddDropdownHidden);
					} else {
						setAdding(true);
					}
				}}
				type="secondary"
			/>
		</Popover.Target>
	);

	return (
		<>
			<SettingsSection title={t('settings.jobCompetencies.title')} actions={dropdown}>
				{!props.jobTitle.competencies.length ? (
					<Empty.Card title={t('settings.jobCompetencies.empty.title')} />
				) : (
					<>
						<Tabs>
							{levels.map((level) => (
								<Tabs.Item
									key={level?.id ?? '*'}
									selected={selectedLevel === level?.id}
									onClick={() => setSelectedLevel(level?.id)}
									title={
										level ? localize(level!.title).text : t('settings.jobCompetencies.allLevels')
									}
								/>
							))}
						</Tabs>
						<Card p={0}>
							{competencies.map((competency) => (
								<JobCompetencyRow
									key={competency.id}
									jobCompetency={competency as graphql.JobCompetency}
									jobTitle={props.jobTitle}
									jobLevelID={selectedLevel}
								/>
							))}
						</Card>
					</>
				)}
			</SettingsSection>
			{adding && (
				<JobCompetencyEditorModal
					jobTitle={props.jobTitle}
					selectedCompetencyIDs={competencies.map((competency) => competency.competency.id)}
					onDismiss={() => setAdding(false)}
				/>
			)}
			{generating && (
				<JobCompetencyGeneratorModal
					jobTitle={props.jobTitle}
					onDismiss={() => setGenerating(false)}
				/>
			)}
		</>
	);
}
